import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { api } from '../api';
import './DashboardPages.css';

function DashboardOverview() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const { analytics: data } = await api.getAnalytics();
        if (!cancelled) setAnalytics(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const chartData = analytics?.weeklyChart ?? [];
  const avgHours = analytics?.avgDeliveryTimeHours;
  const avgDisplay = avgHours != null
    ? `${avgHours.toFixed(1)} hrs`
    : '—';

  return (
    <div className="dashboard-page">
      <h1>Dashboard Overview</h1>
      <p className="dashboard-subtitle">Welcome to the FarmFleet dispatch dashboard</p>

      {error && (
        <div className="orders-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="orders-placeholder">
          <p>Loading analytics…</p>
        </div>
      ) : (
        <>
          <div className="analytics-cards">
            <div className="analytics-card">
              <h3>Total Orders</h3>
              <p className="analytics-value">{analytics?.totalOrders ?? 0}</p>
            </div>
            <div className="analytics-card">
              <h3>Completed Trips</h3>
              <p className="analytics-value">{analytics?.completedTrips ?? 0}</p>
            </div>
            <div className="analytics-card">
              <h3>Avg Delivery Time</h3>
              <p className="analytics-value">{avgDisplay}</p>
            </div>
            <div className="analytics-card">
              <h3>Total Distance</h3>
              <p className="analytics-value">{analytics?.totalDistanceKm ?? 0} km</p>
            </div>
          </div>

          <div className="analytics-cards analytics-cards-secondary">
            <div className="analytics-card analytics-card-gold">
              <h3>Fleet utilization</h3>
              <p className="analytics-value">
                {analytics?.fleetUtilization != null
                  ? `${Math.round(Number(analytics.fleetUtilization) * 100)}%`
                  : '—'}
              </p>
            </div>
            <div className="analytics-card analytics-card-gold">
              <h3>Consolidation rate</h3>
              <p className="analytics-value">
                {analytics?.consolidationRate != null
                  ? `${Math.round(Number(analytics.consolidationRate) * 100)}%`
                  : '—'}
              </p>
            </div>
            <div className="analytics-card analytics-card-gold">
              <h3>Km saved</h3>
              <p className="analytics-value">
                {analytics?.savingsKm != null ? `${Number(analytics.savingsKm).toFixed(0)} km` : '—'}
              </p>
            </div>
          </div>

          <div className="analytics-chart-section">
            <h3 className="analytics-chart-title">Weekly Overview</h3>
            <p className="analytics-chart-subtitle">Orders, trips, and distance over the past 7 days</p>
            <div className="analytics-chart">
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3d8b40" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3d8b40" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorTrips" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#c4952e" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#c4952e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorDistance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#666' }} axisLine={{ stroke: '#ddd' }} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#666' }} axisLine={false} tickLine={false} tickFormatter={(v) => v} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#666' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #eee', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                    labelStyle={{ fontWeight: 600 }}
                    formatter={(value, name) => {
                      const labels = { orders: 'Orders', trips: 'Trips', distance: 'Distance (km)' };
                      return [name === 'distance' ? `${value} km` : value, labels[name] || name];
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: 16 }} iconType="circle" iconSize={8} formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)} />
                  <Area yAxisId="left" type="monotone" dataKey="orders" stroke="#3d8b40" strokeWidth={2} fill="url(#colorOrders)" name="orders" />
                  <Area yAxisId="left" type="monotone" dataKey="trips" stroke="#c4952e" strokeWidth={2} fill="url(#colorTrips)" name="trips" />
                  <Area yAxisId="right" type="monotone" dataKey="distance" stroke="#2e7d32" strokeWidth={2} fill="url(#colorDistance)" name="distance" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DashboardOverview;
